<template>
  <div class="modal fade" @keyup.esc.prevent="closeModal" tabindex="-1" id="archiveItemModal" aria-labelledby="archiveItemModalLabel">
    <div class="modal-dialog modal-lg">
      <div v-if="!item || loaded" class="modal-content shadow-none">
        <loader />
      </div>
      <div v-else class="modal-content shadow-none">
        <div class="modal-header p-5">
          <h5 class="modal-title">{{ item?.orderNum ? 'ID: '+item?.orderNum : 'Устройство' }}</h5>
          <div @click="closeModal" class="btn btn-icon btn-sm btn-active-light-primary ms-2">
            <i class="ki-duotone ki-cross fs-2x"><span class="path1"></span><span class="path2"></span></i>
          </div>
        </div>
        <div class="modal-body p-5">
          <div class="row">
            <div class="col-md-6">
              <ul class="list-group list-group-flush mb-5 rounded border">
                <!-- ОСНОВНАЯ ИНФОРМАЦИЯ -->
                <li class="list-group-item fw-bold text-uppercase">Основное</li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">ID:</span><span>{{ item.id }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">ID Пользователя:</span><span>{{ item.userId }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Тип:</span><span>{{ item.type }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Номер заказа:</span><span>{{ item.orderNum }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Статус:</span><span>{{ item.status.status }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Дата завершения:</span><span>{{ getHumanDate(item.completedAt) }}</span></li>
              </ul>
              <ul class="list-group list-group-flush mb-5 rounded border">
                <!-- СТАНЦИЯ -->
                <li class="list-group-item fw-bold text-uppercase">Локация</li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Название:</span><span>{{ item.spec.details.stationName }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Тип разъема:</span><span>{{ item.spec.details.activeConnector.label }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Номер коннектора:</span><span>{{ item.spec.details.activeConnector.position }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Адрес:</span><span>{{ item.spec.details.stationAddress }}</span></li>
              </ul>
              <ul class="list-group list-group-flush mb-5 rounded border">
                <!-- ФИНАНСЫ -->
                <li class="list-group-item fw-bold text-uppercase">Финансы</li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Плановая сумма:</span><span>{{ formatFullAmount(item.totalAmountPlan) }} сум.</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Сумма факт:</span><span>{{ formatFullAmount(item.totalAmount) }} сум.</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Цена за кВт:</span><span>{{ formatFullAmount(item.price) }} сум.</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Процент (план):</span><span>{{ item.percentPlan }}%</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Процент зарядки (факт):</span><span>{{ item.percent }}%</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Минут (факт):</span><span>{{ item.spec?.progress?.factDurationStr || item.minutesPlan }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Количество (план):</span><span>{{ formatFullAmount(financeVolume(item)) }} кВт•ч</span></li>
              </ul>
              <ul class="list-group list-group-flush mb-5 rounded border">
                <!-- ФИСКАЛЬНЫЕ ДАННЫЕ -->
                <li class="list-group-item fw-bold text-uppercase">Фискальные данные</li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">URL чека:</span>
                  <a :href="item.fiscalUrl" target="_blank" class="text-primary text-decoration-underline">Открыть чек</a>
                </li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Номер фискального чека:</span><span>{{ item.fiscalCheckNumber }}</span></li>
              </ul>
              <ul v-if="item?.contractsInfo?.types" class="list-group list-group-flush mb-5 rounded border">
                <!-- ИНФОРМАЦИЯ О СЧЕТАХ -->
                <li class="list-group-item fw-bold text-uppercase">Информация о счетах</li>
                <li v-if="item.contractsInfo.types.account && hasNonZeroCurrencies(item.contractsInfo.types.account.currencies)" class="list-group-item">
                  <div class="d-flex flex-column gap-2">
                    <div class="fw-semibold text-gray-700">Основной счет:</div>
                    <div class="d-flex flex-column gap-1 ms-3">
                      <div v-for="(currency, key) in getNonZeroCurrencies(item.contractsInfo.types.account.currencies)" :key="key" class="d-flex justify-content-between">
                        <span class="text-gray-500">{{ getCurrencyLabel(key) }}:</span>
                        <span>{{ formatCurrencyAmount(currency.amount, key) }}</span>
                      </div>
                    </div>
                  </div>
                </li>
                <li v-if="item.contractsInfo.types.ticket && hasNonZeroCurrencies(item.contractsInfo.types.ticket.currencies)" class="list-group-item">
                  <div class="d-flex flex-column gap-2">
                    <div class="fw-semibold text-gray-700">Абонемент:</div>
                    <div class="d-flex flex-column gap-1 ms-3">
                      <div v-for="(currency, key) in getNonZeroCurrencies(item.contractsInfo.types.ticket.currencies)" :key="key" class="d-flex justify-content-between">
                        <span class="text-gray-500">{{ getCurrencyLabel(key) }}:</span>
                        <span>{{ formatCurrencyAmount(currency.amount, key) }}</span>
                      </div>
                    </div>
                  </div>
                </li>
                <li v-if="!hasContractsInfo(item)" class="list-group-item">
                  <span class="text-muted">Нет данных о счетах</span>
                </li>
              </ul>
            </div>
            <div class="col-md-6">
              <ul v-if="item.spec.progress" class="list-group list-group-flush mb-5 rounded border">
                <!-- ПРОГРЕСС -->
                <li class="list-group-item fw-bold text-uppercase">Прогресс</li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Сумма факт:</span><span>{{ formatFullAmount(item.spec.progress.factAmount) }} сум.</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Продолжительность факт (сек):</span><span>{{ item.spec.progress.factDuration }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Количество факт:</span><span>{{ formatFullAmount(item.spec.progress.factVolume) }} кВт•ч</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Батарея (конец):</span><span>{{ item.spec.progress.batteryLevel }}%</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Батарея (старт):</span><span>{{ item.spec.progress.batteryLevelStart }}%</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Мощность:</span><span>{{ formatFullAmount(item.spec.progress.currentPower) }} кВт</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Температура:</span><span>{{ item.spec.progress.currentTemperature }} °C</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Факт (время):</span><span>{{ item.spec.progress.factDurationStr }}</span></li>
              </ul>
              <ul v-if="item.spec.firstEvent" class="list-group list-group-flush mb-5 rounded border">
                <!-- ПЕРВОЕ СОБЫТИЕ -->
                <li class="list-group-item fw-bold text-uppercase">Первое событие</li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Статус:</span><span>{{ item.spec.firstEvent.status }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Батарея:</span><span>{{ item.spec.firstEvent.batteryLevel }}%</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Количество:</span><span>{{ formatFullAmount(item.spec.firstEvent.volume) }} кВт•ч</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Мощность:</span><span>{{ formatFullAmount(item.spec.firstEvent.power) }} кВт</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Температура:</span><span>{{ item.spec.firstEvent.temperature }} °C</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Дата и Время:</span><span>{{ getHumanDate(item.spec.firstEvent.ts) }}</span></li>
              </ul>
              <ul v-if="item.spec.lastEvent" class="list-group list-group-flush mb-5 rounded border">
                <!-- ПОСЛЕДНЕЕ СОБЫТИЕ -->
                <li class="list-group-item fw-bold text-uppercase">Последнее событие</li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Статус:</span><span>{{ item.spec.lastEvent.status }}</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Батарея:</span><span>{{ item.spec.lastEvent.batteryLevel }}%</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Количество:</span><span>{{ formatFullAmount(item.spec.lastEvent.volume) }} кВт•ч</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Мощность:</span><span>{{ formatFullAmount(item.spec.lastEvent.power) }} кВт</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Температура:</span><span>{{ item.spec.lastEvent.temperature }} °C</span></li>
                <li class="list-group-item d-flex justify-content-between"><span class="text-gray-500">Дата и Время:</span><span>{{ getHumanDate(item.spec.lastEvent.ts) }}</span></li>
              </ul>
            </div>
          </div>
        </div>
        <div class="modal-footer p-5 justify-content-center">
          <button type="button" @click="closeModal" class="btn btn-light btn-sm">Закрыть</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/components/Loader'
export default {
  name: 'ArchiveModal',
  components: {Loader},
  props: {
    item: {
      type: Object,
      default: null
    },
  },
  data() {
    return {
      loaded: false,
    }
  },
  mounted() {
    setTimeout(() => {
      document.getElementById('archiveItemModal').addEventListener('mousedown', (event) => {
        if (event.target.classList.contains('modal')) this.closeModal()
      });
    }, 10)
  },
  methods: {
    closeModal() {
      bootstrap.Modal.getInstance(document.getElementById('archiveItemModal')).hide();
    },
    getHumanDate(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString('ru-RU');
    },
    formatFullAmount(value) {
      if (!value && value !== 0) return '0.00';
      const number = parseFloat(value);
      if (isNaN(number)) return '0.00';
      return new Intl.NumberFormat('ru-RU', {
        useGrouping: true,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: 'decimal'
      }).format(number);
    },
    formatCurrencyAmount(amount, currencyType) {
      if (!amount || amount === 0) return '0';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0';
      
      if (currencyType === 'kwh') {
        return `${num} кВт•ч`;
      } else if (currencyType === 'uzs') {
        return `${this.formatFullAmount(num)} сум.`;
      } else {
        return `${num} ${currencyType}`;
      }
    },
    hasNonZeroCurrencies(currencies) {
      if (!currencies) return false;
      return Object.values(currencies).some(currency => currency.amount && currency.amount > 0);
    },
    getNonZeroCurrencies(currencies) {
      if (!currencies) return {};
      const filtered = {};
      Object.keys(currencies).forEach(key => {
        if (currencies[key].amount && currencies[key].amount > 0) {
          filtered[key] = currencies[key];
        }
      });
      return filtered;
    },
    getCurrencyLabel(currencyType) {
      const labels = {
        'kwh': 'кВт•ч',
        'uzs': 'Сум'
      };
      return labels[currencyType] || currencyType.toUpperCase();
    },
    financeVolume(item) {
      return item?.volumePlan
        || item?.contractsInfo?.types?.ticket?.currencies?.kwh?.amount
        || item?.contractsInfo?.types?.account?.currencies?.kwh?.amount
        || item?.spec?.progress?.factVolume
        || 0
    },
    hasContractsInfo(item) {
      if (!item?.contractsInfo?.types) return false;
      const account = item.contractsInfo.types.account;
      const ticket = item.contractsInfo.types.ticket;
      return (account && this.hasNonZeroCurrencies(account.currencies)) || 
             (ticket && this.hasNonZeroCurrencies(ticket.currencies));
    }
  }
}
</script>

<style scoped>

</style>